import useTranslation from '../useTranslation';
import { useEffect, useState } from 'react';
import { formatToUSShortDateTime } from 'utils/dateTimeUtils/dateFormatUtil';

import usePartTypeLabels from 'hooks/usePartTypeLabels';
import useServiceLogs from 'hooks/useServiceLogs';
import useUser from 'hooks/useUser';

import type { PerformerNames, Props, ServiceLogTarget, UseData } from './types';

const useData = ({ drone }: Props): UseData => {
    const { ALL_UNITS, CALIBRATION_KINDS } = useTranslation();
    const { getPartTypeLabel } = usePartTypeLabels();
    const { fetch, isLoading, reset, serviceLogs } = useServiceLogs();
    const { fetchUser } = useUser();

    const [performerNames, setPerformerNames] = useState<PerformerNames>({});

    const refetch = () => {
        reset();
        fetch({ assetId: drone.id }).next();
    };

    useEffect(() => {
        refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drone.id]);

    useEffect(() => {
        const unresolvedIds = [
            ...new Set((serviceLogs ?? []).map(({ createdBy }) => createdBy))
        ].filter(id => !(id in performerNames));

        if (!unresolvedIds.length) {
            return;
        }

        const resolvePerformers = async () => {
            const users = await Promise.all(unresolvedIds.map(id => fetchUser({ id })));

            const resolved = users.reduce<PerformerNames>((names, user) => {
                if (!user) {
                    return names;
                }

                const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');

                return { ...names, [user.id]: fullName || user.email };
            }, {});

            setPerformerNames(current => ({ ...current, ...resolved }));
        };

        resolvePerformers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceLogs]);

    const getUnitSuffix = (unitIndex?: number) => (unitIndex ? ` ${unitIndex}` : '');

    const getPartLabel = ({ calibrationKind, partType, unitIndex }: ServiceLogTarget) => {
        if (partType) {
            const partName = getPartTypeLabel(partType);

            return unitIndex ? `${partName} ${unitIndex}` : `${partName} (${ALL_UNITS})`;
        }

        if (calibrationKind) {
            return `${CALIBRATION_KINDS[calibrationKind]}${getUnitSuffix(unitIndex)}`;
        }

        return undefined;
    };

    const entries = (serviceLogs ?? []).map(
        ({
            calibrationKind,
            createdBy,
            eventType,
            id,
            modelNumber,
            notes,
            partType,
            performedAt,
            unitIndex
        }) => ({
            eventType,
            id,
            modelNumber,
            notes,
            partLabel: getPartLabel({ calibrationKind, partType, unitIndex }),
            performedAt: formatToUSShortDateTime(new Date(performedAt)),
            performedBy: performerNames[createdBy]
        })
    );

    return { entries, isLoading, refetch };
};

export default useData;
